import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/logo-buunpo/BUUNPO - LOGO FUNDO - MOD 03.png';
import { ReactComponent as LupaIcon } from '../../assets/icons/lupa.svg';
import { ReactComponent as CartIcon } from '../../assets/icons/cart.svg';
import { ReactComponent as BellIcon } from '../../assets/icons/bell.svg';
import { ReactComponent as HelpIcon } from '../../assets/icons/help-circle.svg';
import { ReactComponent as DefaultUser } from '../../assets/icons/user3.svg';
import { UseFormReturn } from 'src/hooks';
import { Submenu } from '../Submenu';
import { Dropdown2 } from '../Dropdown2';
import { Search2 } from '../Search2';
import SocialLinks from '../SocialLinks';
import AppQrCode from '../AppQrCode';
import { QrCodeData } from 'src/model/QrCode';
import { Button } from '../Button';
import { ProductListUI } from 'src/features/checkout/screens/Checkout/components/ProductList/ui';
import { Cart } from 'src/model/Cart';
import { api } from 'src/services/api';
import { getBoolean } from 'src/helpers/common/localStorage';
import { REACT_APP_AUTH } from 'src/utils/config';
import { NotificationComponentScreen } from '../NotificationComponent';
import { AlertMessage } from 'src/model/AlertMessage';
import AppDownloadBannerInline from '../Header/AppDownloadBannerInline';
import { toast } from 'react-toastify';
import { Badge } from '../Badge/Badge';

interface HeaderMenuShopeeProps
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  signed: boolean;
  carts: Cart[];
  cartCount: number;
  onSubmitSearch: (e: FormEvent) => void;
  onLinkClick: (href: string) => void;
  qrCode?: QrCodeData;
  onSubmitLogout: () => void;
  alertMessage: AlertMessage[];
}

type StoreInfo = {
  id?: string | number;
  productsApprovedCount?: number;
  status?: string;
};

type HeaderUser = {
  name: string;
  type: string;
  image: string;
  email: string;
};

export const HeaderMenuShopee: React.FC<HeaderMenuShopeeProps> = ({
  qrCode,
  formData,
  formErrors,
  signed,
  cartCount,
  carts,
  alertMessage,
  onChangeFormInput,
  onSubmitSearch,
  onLinkClick,
  onSubmitLogout,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [users, setUsers] = useState<HeaderUser[]>([]);
  const [hasStore, setHasStore] = useState(false);
  const [storeInfo, setStoreInfo] = useState<StoreInfo | null>(null);

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      mobileMenuRef.current &&
      !mobileMenuRef.current.contains(event.target as Node) &&
      menuButtonRef.current &&
      !menuButtonRef.current.contains(event.target as Node)
    ) {
      setIsMobileMenuOpen(false);
    }
  };

  const fetchStore = async (): Promise<void> => {
    try {
      const r = await api.get('/store/find-store');
      if (r.status === 200 && r.data) {
        setHasStore(true);
        const d = r.data as StoreInfo;
        setStoreInfo({
          id: d?.id,
          productsApprovedCount:
            typeof d?.productsApprovedCount === 'number'
              ? d.productsApprovedCount
              : Array.isArray((d as any)?.products)
                ? (d as any).products.filter(
                    (p: any) => p?.status === 'APPROVED'
                  ).length
                : 0,
          status: d?.status,
        });
      } else {
        setHasStore(false);
        setStoreInfo(null);
      }
    } catch {
      setHasStore(false);
      setStoreInfo(null);
    }
  };

  const getUser = async (): Promise<void> => {
    try {
      const r = await api.get<HeaderUser>('/user/summary');
      if (r.status === 200) setUsers([r.data]);
    } catch {
      console.error('Erro ao buscar o usuario');
    }
  };

  useEffect(() => {
    if (isMobileMenuOpen)
      document.addEventListener('mousedown', handleOutsideClick);
    else document.removeEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isMobileMenuOpen]);

  const [socialLinks, setSocialLinks] = useState({
    instagram: '',
    whatsapp: '',
    tiktok: '',
    youtube: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get('/social-media/find-all');
        if (r.status === 200) {
          const { instagram, whatsapp, tiktok, youtube } = r.data;
          setSocialLinks({ instagram, whatsapp, tiktok, youtube });
        }
      } catch {
        console.error('Erro ao carregar as redes sociais');
      }
    };
    load();
  }, []);

  const isAuth = async (): Promise<void> => {
    setTimeout(async () => {
      if (getBoolean(String(REACT_APP_AUTH)) !== signed) return;
      else isAuth();
    }, 500);
  };

  useEffect(() => {
    isAuth();
  }, [signed]);

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    if (signed) fetchStore();
  }, [signed]);

  const shouldShowStoreProfile =
    users[0]?.type === 'SELLER' || users[0]?.type === 'ADMIN';

  const handleProtectedLink = async (href: string) => {
    if (href !== '/store-profile') {
      onLinkClick(href);
      return;
    }
    await fetchStore();
    if (!hasStore) {
      onLinkClick('/user-profile?tab=dados-loja');
      toast.info(
        'Você ainda não possui uma loja. Preencha os dados no formulário para criar uma loja'
      );
      return;
    }
    let productResponse;
    try {
      productResponse = await api.get(`/product/find-store/`);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        productResponse = { status: 404 };
      } else {
        toast.error(
          'Erro ao verificar produtos da loja. Tente novamente mais tarde.'
        );
        return;
      }
    }

    const noProducts = productResponse.status === 404;

    if (noProducts) {
      onLinkClick('/user-profile?tab=criar-produto');
      toast.info(
        'Sua loja já foi criada. Agora cadastre pelo menos 1 produto e quando ele for aprovado sua loja será liberada'
      );
      return;
    }
    onLinkClick('/store-profile');
  };

  const submenuLinks = [
    { text: 'Perfil', href: '/user-profile' },
    ...(shouldShowStoreProfile
      ? [{ text: 'Perfil da Loja', href: '/store-profile' }]
      : []),
    { text: 'Tutorial', href: '/tutorial' },
    { text: 'Política de Privacidade', href: '/privacy-policy' },
    { text: 'Política de Anúncios', href: '/advertising' },
  ];

  const profilePhotoUrl = `${process.env.REACT_APP_BASE_URL}/uploads/${users[0]?.image}`;

  return (
    <header className="w-full bg-[#F14A3E]  z-40">
      <div className="hidden md:block w-full container mx-auto px-4 py-2">
        <div className="flex justify-between items-center">
          <div className="flex w-full items-center ml-20 gap-4">
            <span className="text-white">Siga-nos no</span>
            <SocialLinks links={socialLinks} />
            {qrCode && <AppQrCode qrCode={qrCode} />}
          </div>

          <div className="flex items-center gap-6">
            <Dropdown2
              triggerContent={
                <div className="flex items-center space-x-2">
                  <BellIcon className="w-6 h-6" />
                  <span className="text-white text-sm font-semibold">
                    Notificações
                  </span>
                </div>
              }
              notificationsCount={alertMessage.length}
            >
              <NotificationComponentScreen
                alertMessage={alertMessage}
                variant="dropdown"
              />
            </Dropdown2>

            <Link to={'/'} className="flex items-center gap-2">
              <HelpIcon className="w-6 h-6" />
              <span className="text-white text-sm font-semibold">Ajuda</span>
            </Link>
            {signed ? (
              <Dropdown2
                triggerContent={
                  <div className="flex items-center space-x-2">
                    {profilePhotoUrl ? (
                      <img
                        className="w-8 h-8 rounded-full"
                        src={profilePhotoUrl}
                        alt="User photo"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '';
                        }}
                      />
                    ) : (
                      <DefaultUser className="w-8 h-8" />
                    )}
                    <span className="text-white text-sm font-semibold">
                      Olá, {users[0]?.name.split(' ')[0]}!
                    </span>
                  </div>
                }
                userName={users[0]?.name}
                userEmail={users[0]?.email}
              >
                <Submenu
                  links={submenuLinks}
                  onLinkClick={handleProtectedLink}
                  className="p-2.5 px-5 flex-col bg-[#ffffffb5] rounded-md text-black fade-in divide-y divide-gray-200"
                  liClassName="scale-zoom-1 hover:text-gray-400 p-1"
                />
                <div className="w-full flex justify-center items-center mt-3 mb-3">
                  <Button
                    type="button"
                    title="Sair"
                    buttonStyle="outline"
                    size="sm"
                    onClick={onSubmitLogout}
                  />
                </div>
              </Dropdown2>
            ) : (
              <Dropdown2
                triggerContent={
                  <div className="flex items-center space-x-2">
                    <DefaultUser className="w-6 h-6" />
                    <span className="text-white text-sm font-semibold">
                      Entrar
                    </span>
                  </div>
                }
              >
                <Submenu
                  links={[
                    { text: 'Login', href: '/login' },
                    { text: 'Criar Conta', href: '/create-account' },
                  ]}
                  onLinkClick={onLinkClick}
                  className="p-2.5 px-5 flex-col bg-[#ffffffb5] rounded-md text-black fade-in divide-y divide-gray-200"
                  liClassName="scale-zoom-1 hover:text-gray-400 p-1"
                />
              </Dropdown2>
            )}
          </div>
        </div>
      </div>

      <AppDownloadBannerInline
        iosUrl="https://apps.apple.com/br/app/buunpo-marketplace/id6749515686"
        androidUrl="https://play.google.com/store/apps/details?id=com.webviewgold.buunpo"
        title="Abra o app e ganhe frete grátis na sua primeira compra"
        ctaLabel="Abrir"
        appIconUrl="/buunpologo.jpeg"
      />

      <div className="md:block hidden w-full container mx-auto px-4 py-3">
        <div className="flex items-center justify-between md:justify-center gap-4">
          <div className="flex items-center md:block hidden ">
            <Link to="/">
              <img
                src={logo}
                alt="Logo"
                className="w-32 md:w-52 pulse cursor-pointer"
              />
            </Link>
          </div>

          <div className="hidden md:block flex-1 max-w-2xl">
            <Search2
              formData={formData}
              formErrors={formErrors}
              onChangeFormInput={onChangeFormInput}
              onSubmitSearch={onSubmitSearch}
              icon={<LupaIcon />}
            />
          </div>
          <div className="hidden md:block  ml-4">
            <a href="/carts">
              <Dropdown2
                triggerContent={<CartIcon className="w-6 h-6 text-white" />}
                notificationsCount={cartCount}
                width="400px"
              >
                {carts && carts.length > 0 && (
                  <ProductListUI cartsProducts={carts} />
                )}
              </Dropdown2>
            </a>
          </div>
        </div>
      </div>

      <div className="md:hidden grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 p-2 w-full">
        <div className="w-full">
          <Search2
            className="h-9 p-1"
            formData={formData}
            formErrors={formErrors}
            onChangeFormInput={onChangeFormInput}
            onSubmitSearch={onSubmitSearch}
            icon={<LupaIcon />}
          />
        </div>

        <a
          href="/carts"
          className="relative w-9 h-9 rounded-full flex items-center justify-center"
        >
          <CartIcon className="w-5 h-5 text-white" />
          <span className="absolute -top-1.5 -right-1.5 bg-white text-[#F14A3E] rounded-full min-w-[16px] h-[16px] text-[10px] leading-[16px] text-center px-[3px]">
            {cartCount > 99 ? '99+' : cartCount}
          </span>
        </a>

        {signed ? (
          <a
            href="/profile"
            className="relative w-9 h-9 rounded-full flex items-center justify-center"
          >
            <div className="w-9 h-9 rounded-full overflow-hidden">
              {profilePhotoUrl ? (
                <>
                  <img
                    className="w-9 h-9 object-cover"
                    src={profilePhotoUrl}
                    alt="User photo"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '';
                    }}
                  />
                  {alertMessage?.length > 0 && (
                    <span
                      aria-hidden
                      className="absolute top-0 right-0 w-2 h-2 bg-[#F14A3E] rounded-full ring-2 ring-white translate-x-1/4 -translate-y-1/4 shadow-sm"
                    />
                  )}
                </>
              ) : (
                <DefaultUser className="w-6 h-6 text-white" />
              )}
            </div>
          </a>
        ) : (
          <a
            href="/profile"
            className="w-9 h-9 rounded-full flex items-center justify-center"
          >
            <DefaultUser className="w-6 h-6 text-white" />
          </a>
        )}
      </div>
    </header>
  );
};
